/**
 * Per-hero Backup Meter summon configurations (MY-1784).
 *
 * Maps each hero id to a themed SummonConfig describing which temporary
 * allies spawn when the player activates the Backup Meter (MY-1781).
 *
 * Roles and subcultures only — never ethnicities or nationalities
 * (project rule). Pure module: no Phaser import.
 */

/** The role/type of a temporary ally unit. */
export type AllyKind = 'crew' | 'dog' | 'rifleman' | 'fighter' | 'operative';

/**
 * Optional one-shot area pulse triggered at summon time.
 *  'bass-stun' — briefly stuns nearby enemies (Errol's sound-system).
 *  'heal'      — restores a small amount of green health to the active player.
 */
export type SummonEffect = 'bass-stun' | 'heal';

/** Describes a homogeneous batch of allies. */
export interface AllySpec {
  kind: AllyKind;
  count: number;
}

/** Full Backup Meter summon configuration for one hero. */
export interface SummonConfig {
  /** HUD / callout label shown when the summon activates. */
  label: string;
  /** One or more batches of allies to spawn. */
  allies: AllySpec[];
  /**
   * Optional pulse effect triggered at summon time.
   * Undefined = no special effect beyond ally spawn.
   */
  effect?: SummonEffect;
}

/** Fallback used when no hero-specific config matches (pickup chars, etc.). */
export const DEFAULT_SUMMON_CONFIG: Readonly<SummonConfig> = {
  label: 'K9 Dog Squad',
  allies: [{ kind: 'dog', count: 2 }],
};

/**
 * Hero-specific summon table.
 * Keys match hero ids in src/data/heroes.ts.
 */
const HERO_SUMMON_MAP: Readonly<Record<string, SummonConfig>> = {
  // Ray "Tank" Miller — football-terrace firm, armed with bats.
  ray: {
    label: 'The Firm',
    allies: [{ kind: 'crew', count: 3 }],
  },
  // Errol "Dread" Clarke — carnival/dub posse; bass-stun pulse on arrival.
  errol: {
    label: 'Sound-System Crew',
    allies: [{ kind: 'crew', count: 3 }],
    effect: 'bass-stun',
  },
  // Kris Kowalczyk — ex-forces riflemen, ranged support.
  kris: {
    label: 'British Army',
    allies: [{ kind: 'rifleman', count: 2 }],
  },
  // Priya "Fury" Kaur — Muay Thai gym crew; heal pulse for the team.
  priya: {
    label: 'Gym Crew',
    allies: [{ kind: 'fighter', count: 2 }],
    effect: 'heal',
  },
  // Ghost — SAS operative + K9 pair.
  ghost: {
    label: 'SAS + K9',
    allies: [
      { kind: 'operative', count: 1 },
      { kind: 'dog', count: 2 },
    ],
  },
};

/**
 * Return the summon config for the given hero id.
 * Falls back to DEFAULT_SUMMON_CONFIG for null or unknown ids.
 */
export function getHeroSummonConfig(heroId: string | null): SummonConfig {
  if (heroId === null) return DEFAULT_SUMMON_CONFIG;
  return HERO_SUMMON_MAP[heroId] ?? DEFAULT_SUMMON_CONFIG;
}

/** Expose the map for tests that enumerate all defined heroes. */
export const DEFINED_HERO_IDS: readonly string[] = Object.keys(HERO_SUMMON_MAP);
